import React, {
  useCallback, useEffect,
  useMemo,
  useRef
} from 'react';
import { useClientStore } from '../../../../hooks/ClientState';
import {
  Box, SxProps,
  Theme,
  Button
} from '@mui/material';
import { DebugView } from './DebugView';
import { useShallow } from 'zustand/react/shallow';
import DatasetView from './DatasetView';
import { CanvasDisplaySelection } from '../../../../types/ClientState';

export function CanvasView(props: {
  debug?: boolean;
  sx?: SxProps<Theme>;
  // triggered when the canvas tab is clicked to change the display
  onCanvasChange?: (selection: CanvasDisplaySelection) => void;
  // triggered when the hide button is clicked
  onCanvasClose?: () => void;
}): React.ReactElement {
  const canvasDisplay = useClientStore(useShallow(state => state.canvasDisplay));
  const datasets = useClientStore(useShallow(state => state.datasets));
  const setCanvasDisplay = useClientStore(state => state.setCanvasDisplay);

  // Add available canvas tabs based on content
  const availableTabs = useMemo(() => {
    const tabs = [];

    // Debug view tab
    if (props.debug) {
      tabs.push({ id: 'debug', label: 'Debug' });
    }

    // Dataset tabs
    Object.keys(datasets).forEach(datasetName => {
      tabs.push({ id: `dataset-${datasetName}`, label: datasetName });
    });

    return tabs;
  }, [props.debug, datasets]);

  // Derive the active tab ID from canvasDisplay state (source of truth)
  const activeTab = useMemo(() => {
    if (!canvasDisplay) return '';

    if (canvasDisplay.type === 'debug') {
      return 'debug';
    }
    else if (canvasDisplay.type === 'dataset') {
      return `dataset-${canvasDisplay.name}`;
    }

    return '';
  }, [canvasDisplay]);

  const onCanvasChangeRef = useRef(props.onCanvasChange);
  useEffect(() => {
    onCanvasChangeRef.current = props.onCanvasChange;
  }, [props.onCanvasChange]);

  // Handle selecting a different tab
  const handleTabSelect = useCallback((tabId: string) => {
    if (tabId === 'debug') {
      setCanvasDisplay({ type: 'debug' });
      onCanvasChangeRef.current?.({ type: 'debug' });
    } else if (tabId.startsWith('dataset-')) {
      const datasetName = tabId.replace('dataset-', '');
      setCanvasDisplay({ type: 'dataset', name: datasetName });
      onCanvasChangeRef.current?.({ type: 'dataset', name: datasetName });
    }
  }, [setCanvasDisplay]);

  // Check if we have any content to display
  const hasCanvasContent = useMemo(() => {
    return availableTabs.length > 0;
  }, [availableTabs]);

  // Handle hide canvas button click
  const handleHideCanvas = useCallback(() => {
    setCanvasDisplay(null);
    props.onCanvasClose?.();
  }, [setCanvasDisplay, props.onCanvasClose]);

  // Get the dataset for display if selected
  const selectedDataset = useMemo(() => {
    if (canvasDisplay?.type === 'dataset') {
      return datasets[canvasDisplay.name];
    }
    return null;
  }, [canvasDisplay, datasets]);

  // If we have no content or display is explicitly set to null, hide the canvas
  if (!hasCanvasContent || canvasDisplay === null) {
    return <></>;
  }

  return (
    <Box sx={{
      // IMPORTANT: we need to set default width if it's not in sx, otherwise datagrid would have problem to figure out its width
      // you'd observe ever growing datagrid width if the width is not set here
      width: '100%',
      ...props.sx,
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
    }}>
      {/* Canvas header with tabs and hide button */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        mb: 1,
      }}>
        {/* Tabs for different canvas types */}
        <Box sx={{ flex: 1, display: 'flex', overflow: 'auto' }}>
          {availableTabs.map(tab => (
            <Button
              key={tab.id}
              onClick={() => handleTabSelect(tab.id)}
              sx={{
                py: 0.5,
                px: 2,
                borderRadius: 0,
                borderBottom: activeTab === tab.id ? 2 : 0,
                borderColor: 'primary.main',
                color: activeTab === tab.id ? 'primary.main' : 'text.secondary',
                textTransform: 'none',
                minWidth: 'unset'
              }}
            >
              {tab.label}
            </Button>
          ))}
        </Box>

        {/* Hide button */}
        <Button
          onClick={handleHideCanvas}
          sx={{
            minWidth: 'unset',
            p: 0.5,
            textTransform: 'none',
          }}
          title="Hide canvas"
        >
          hide
        </Button>
      </Box>

      {/* Canvas content */}
      <Box sx={{ overflow: 'auto', flexGrow: 1 }}>
        {canvasDisplay?.type === 'debug' && <DebugView width='100%' />}
        {canvasDisplay?.type === 'dataset' && selectedDataset && (
          <DatasetView
            dataSchema={selectedDataset.dataSchema}
            rows={selectedDataset.rows}
            editableColumns={selectedDataset.editableColumns}
          />
        )}
      </Box>
    </Box>
  );
}
